import { ConfirmDeleteModalComponent } from '$core/modal/confirm-delete/confirm-delete.component';
import { Pagination } from '$core/models/pagination';
import { SwalService } from '$core/shell/swal/swal.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap/modal';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { filter, finalize, first, switchMap } from 'rxjs/operators';
import { ManageBatchReceiptComponent } from '../../components/manage-batch-receipt/manage-batch-receipt.component';
import { InvoicesService } from '../../services';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';
import { Invoice } from '$modules/personal/modules/batch/features/invoices/models/invoice';
import { PaginationParams } from '$shared/components/paginator/paginator.component';
import { InvoicesQueryParams } from '$modules/personal/modules/batch/features/addresses/models/batch-recipient';

@Component({
  selector: 'app-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IndexComponent implements OnInit, OnDestroy {
  public items: Pagination<Invoice>;
  public selected: number[];
  public isLoading = false;
  public queryParams: InvoicesQueryParams;
  private isViewDestroyed = false;

  constructor(
    private readonly _invoicesService: InvoicesService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _modalService: BsModalService,
    private readonly _translateService: TranslateService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _swalService: SwalService,
    private readonly _toastr: ToastrService
  ) {}

  public ngOnInit(): void {
    this.listenToRouter();
  }

  public ngOnDestroy(): void {
    this.isViewDestroyed = true;
  }

  public createInvoice(): void {
    this._modalService.show(ManageBatchReceiptComponent, {
      class: 'modal-lg modal-dialog-centered',
    });
    this._modalService.onHidden
      .pipe(first(), untilDestroyed(this))
      .subscribe((res) => {
        if (res === 'saved') {
          this.getInvoices();
        }
      });
  }

  public search(search: string | null): Promise<boolean> {
    return this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        perPage: this.queryParams.perPage,
        search,
      },
    });
  }

  public removeInvoices(): void {
    const ids = this.items.data
      .filter((el) => el.selected)
      .map((item) => item.id);
    this._modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title:
          this._translateService.instant(
            'BATCH_MAILING.RECEIPTS_TAB.DELETE_RECEIPT_TITLE'
          ) + '?',
        subTitle: 'BATCH_MAILING.RECEIPTS_TAB.DELETE_RECEIPT_SUBTITLE',
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this._modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete'),
        switchMap(() => this._invoicesService.removeInvoices({ ids })),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.getInvoices();

          this._toastr.success(
            this._translateService.instant(
              'BATCH_MAILING.RECEIPTS_TAB.RECEIPT_DELETED'
            )
          );
        },
        (err) => {
          return this.onError(err);
        }
      );
  }

  public pageChanged(ev: PaginationParams): Promise<boolean> {
    return this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { ...this.queryParams, ...ev },
    });
  }

  public updateSelection(): void {
    if (this.items && this.items.data) {
      this.selected = this.items.data
        .filter((el) => el.selected)
        .map((el) => el.id);
    }
  }

  private listenToRouter(): void {
    this._route.queryParams.pipe(untilDestroyed(this)).subscribe((res) => {
      this.queryParams = {
        ...res,
        perPage: validateLocalStoragePerPage('batch_items_per_page'),
      };
      this.getInvoices();
    });
  }

  private getInvoices(): void {
    this.isLoading = true;
    this._invoicesService
      .getInvoices(this.queryParams)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          if (!this.isViewDestroyed) {
            this._cd.detectChanges();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe(
        (res) => {
          this.items = res;
          this.updateSelection();
        },
        (err) => {
          return this.onError(err);
        }
      );
  }

  private onError(error: HttpErrorResponse) {
    const res: HttpErrorResponse = error;
    const swal = this._swalService.swal;
    swal.icon = 'error';
    swal.title = this._translateService.instant('AN_ERROR_HAS_OCCURRED');
    swal.text = res.error
      ? res.error.message || res.error.error
      : res.statusText;
    return swal.fire();
  }
}
